/**
 * Dev-only viewport button — open the game at exactly iPad landscape.
 *
 * Lives on the shared dev strip (`devBar.js`), which owns the localhost gate,
 * the positioning and the chip styling.
 *
 * WHY A POPUP, and not a clamped container. A page cannot resize its own tab:
 * `window.resizeTo` is ignored unless the window was opened by script. The
 * tempting alternative — constraining #game-container to 1024x768 and calling
 * that a preview — would LIE, because this layout leans on things that read the
 * real viewport and not the container:
 *
 *   @media (max-height: 820px)   the catalog-card and pane-blurb compression
 *   calc(100vh - var(--map-chrome))  the world map's size
 *   subTabs.js sizeActiveScroller    window.innerHeight
 *
 * All three would keep reporting the desktop viewport, so the clamped box would
 * render the DESKTOP layout at tablet dimensions — the exact opposite of what a
 * tablet check is for. A script-opened window has a real viewport, and may then
 * be resized to correct for browser chrome.
 */

import { devBar, devButton } from './devBar.js';

const PRESET = { label: 'iPad landscape', w: 1024, h: 768 };
const WINDOW_NAME = 'idle-extraction-ipad-landscape';

/**
 * Resize until the CONTENT area matches, not the outer window.
 *
 * `window.open`'s width/height are interpreted inconsistently — some browsers
 * size the outer frame, so asking for 768 can leave ~700 of usable height once
 * the toolbar is subtracted. Correcting by the measured delta is the only way to
 * land on the number that actually matters, which is innerWidth/innerHeight.
 */
function fitViewport(w, h) {
    window.resizeBy(w - window.innerWidth, h - window.innerHeight);
}

function atPreset() {
    return window.innerWidth === PRESET.w && window.innerHeight === PRESET.h;
}

function label() {
    return `${atPreset() ? '✅' : '📐'} ${window.innerWidth}×${window.innerHeight}`;
}

export function initDevViewport() {
    const bar = devBar();
    if (!bar) return;

    const btn = devButton({
        label: label(),
        title: `Dev only — open the game in a window sized to ${PRESET.label} (${PRESET.w}×${PRESET.h}). Shows the live viewport size.`,
    });
    btn.id = 'dev-viewport-btn';
    btn.style.marginRight = '4px';

    btn.addEventListener('click', () => {
        // Already in the sized window: re-fit in place. Handy after a manual
        // drag, and it is the only case where resizing is permitted at all.
        if (window.name === WINDOW_NAME) {
            fitViewport(PRESET.w, PRESET.h);
            return;
        }
        const popup = window.open(
            location.href, WINDOW_NAME,
            `width=${PRESET.w},height=${PRESET.h},menubar=no,toolbar=no,location=no,status=no`
        );
        if (!popup) {
            btn.textContent = '📐 popup blocked';
            return;
        }
        popup.focus();
    });

    // Doubles as a live readout, which is what makes this useful even where the
    // popup is blocked or the browser absorbs window.open: drag the window until
    // it goes green and you are at iPad landscape.
    const refresh = () => {
        btn.textContent = label();
        btn.style.color = atPreset() ? '#4ade80' : '#9ca3af';
        btn.style.borderColor = atPreset() ? '#166534' : '#374151';
    };
    window.addEventListener('resize', refresh);
    bar.appendChild(btn);
    refresh();

    // The popup lands at whatever size the browser chose; correct it once it
    // has laid out, then report the real number.
    if (window.name === WINDOW_NAME) {
        requestAnimationFrame(() => { fitViewport(PRESET.w, PRESET.h); refresh(); });
    }
}
